import React from "react";
import UiIcon from "./UiIcon";
import { UiIconName } from "../utils/uiIcons";

// Petit kit visuel (charte OLTODO "à la Uber" : noir/blanc/gris, grands titres, boutons pleins,
// cartes à filet fin, aucune couleur d'accent) partagé par les modules Anniversaire et
// Vérification. Volontairement local à ces deux modules pour l'instant — le reste de l'app garde
// ses propres styles inline tant qu'il n'a pas été refondu. Miroir de OLTODO/components/UiKit.tsx.
export const K = { ink: "#0a0a0a", muted: "#6B6B6B", faint: "#9B9B9B", line: "#EBEBEB", surface: "#EEEEEE", white: "#FFFFFF", danger: "#C0392B" };

// Conteneur de page : colonne 560px, jamais plus large que l'écran (minWidth:0 + overflowX:clip
// coupent net tout débordement horizontal — c'était la cause du "la page se balade" sur iPhone,
// où les <input type=date/time> ont une largeur intrinsèque supérieure à leur colonne flex).
export function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", padding: "12px 16px 96px", boxSizing: "border-box", minWidth: 0, overflowX: "clip" as any }}>
      {children}
    </div>
  );
}

export function PageHeader({ title, onBack, right }: { title: string; onBack?: () => void; right?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, minWidth: 0 }}>
      {onBack && (
        <button onClick={onBack} aria-label="Retour" style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: K.ink }}>
          <UiIcon name="back" size={20} />
        </button>
      )}
      <h1 style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 700, letterSpacing: "-0.02em", flex: 1, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflowWrap: "anywhere", color: K.ink }}>{title}</h1>
      {right}
    </div>
  );
}

export function RoundButton({ icon, onClick, label, dark }: { icon: UiIconName; onClick: () => void; label: string; dark?: boolean }) {
  return (
    <button onClick={onClick} aria-label={label} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: dark ? K.ink : K.surface, color: dark ? K.white : K.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <UiIcon name={icon} size={20} />
    </button>
  );
}

export function Card({ children, style, onClick }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={{ backgroundColor: K.white, border: `1px solid ${K.line}`, borderRadius: 16, padding: 16, marginBottom: 12, minWidth: 0, cursor: onClick ? "pointer" : undefined, ...style }}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ margin: "4px 0 10px" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: K.ink }}>{children}</div>
      {hint && <div style={{ fontSize: 12.5, color: K.muted, marginTop: 2, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

type BtnVariant = "primary" | "secondary" | "danger" | "soft";
export function Button({ children, onClick, disabled, variant = "primary", icon, small, style }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; variant?: BtnVariant; icon?: UiIconName; small?: boolean; style?: React.CSSProperties }) {
  const palette: Record<BtnVariant, { bg: string; fg: string; border: string }> = {
    primary: { bg: K.ink, fg: K.white, border: K.ink },
    secondary: { bg: K.white, fg: K.ink, border: K.line },
    soft: { bg: K.surface, fg: K.ink, border: K.surface },
    danger: { bg: K.white, fg: K.danger, border: K.danger },
  };
  const c = palette[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{ width: "100%", height: small ? 42 : 52, borderRadius: 12, backgroundColor: c.bg, color: c.fg, border: `1.5px solid ${c.border}`, fontSize: small ? 13.5 : 15, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: disabled ? 0.4 : 1, cursor: disabled ? "default" : "pointer", ...style }}>
      {icon && <UiIcon name={icon} size={small ? 16 : 18} />}
      {children}
    </button>
  );
}

// Pilule d'information (rôle, statut, mode surprise...) — grise par défaut, "ink" pour un état actif.
export function Chip({ children, icon, ink }: { children: React.ReactNode; icon?: UiIconName; ink?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 24, padding: "0 9px", borderRadius: 12, backgroundColor: ink ? K.ink : K.surface, color: ink ? K.white : K.ink, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      {icon && <UiIcon name={icon} size={12} strokeWidth={2.1} />}
      {children}
    </span>
  );
}

// Onglets segmentés (piste grise, onglet actif blanc surélevé).
export function Segmented<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: string; icon?: UiIconName }[]; value: T; onChange: (k: T) => void }) {
  return (
    <div style={{ display: "flex", gap: 4, padding: 4, borderRadius: 14, backgroundColor: K.surface, marginBottom: 16 }}>
      {tabs.map(tb => {
        const active = tb.key === value;
        return (
          <button key={tb.key} onClick={() => onChange(tb.key)} style={{ flex: 1, minWidth: 0, height: 38, borderRadius: 10, backgroundColor: active ? K.white : "transparent", boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none", color: active ? K.ink : K.muted, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, overflow: "hidden" }}>
            {tb.icon && <UiIcon name={tb.icon} size={15} />}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tb.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function InfoRow({ icon, label, value, last }: { icon: UiIconName; label: string; value: string; last?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: last ? "none" : `1px solid ${K.line}`, minWidth: 0 }}>
      <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: K.ink }}>
        <UiIcon name={icon} size={18} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11.5, color: K.faint, marginBottom: 1 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: K.ink, overflowWrap: "anywhere" }}>{value}</div>
      </div>
    </div>
  );
}

export function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button role="switch" aria-checked={value} onClick={() => onChange(!value)} style={{ position: "relative", width: 48, height: 28, borderRadius: 14, backgroundColor: value ? K.ink : "#D9D9D9", flexShrink: 0, transition: "background-color .2s" }}>
      <span style={{ position: "absolute", top: 3, left: value ? 23 : 3, width: 22, height: 22, borderRadius: 11, backgroundColor: K.white, transition: "left .2s", boxShadow: "0 1px 2px rgba(0,0,0,0.25)" }} />
    </button>
  );
}

// Champ texte — étiquette au-dessus, jamais flottante ; minWidth:0 + appearance:none pour que les
// champs date/heure natifs de Safari iOS restent dans leur colonne.
export const fieldStyle: React.CSSProperties = { width: "100%", minWidth: 0, maxWidth: "100%", height: 50, padding: "0 14px", borderRadius: 12, border: `1.5px solid ${K.line}`, backgroundColor: K.white, fontSize: 15, color: K.ink, boxSizing: "border-box", WebkitAppearance: "none", appearance: "none" as any };

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14, minWidth: 0 }}>
      <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: K.muted, marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}
